import { useCallback, useState } from "react";
import { toast } from "sonner";
import api from "../../../lib/apiClient";
import { cacheInvalidate } from "../../../lib/cache";
import { clearDraft, DEFAULT_LOCATION } from "../../../lib/drafts";

// useRecorderPublish — owns the "Publish" button's two-path flow:
//   • Commission path: POST /commissions/:id/submit, expects bounty.
//   • Free-form path:  POST /videos/upload, charges credits.
// Extracted from VideoRecorderDialog (Iter-58.6 split) so the parent
// file shrinks under the 600-line guard and the publish logic can
// evolve in isolation (e.g. when we add chunked upload).
//
// Returns:
//   creating       — boolean flag for the spinner / disabled state.
//   handlePublish  — async submit fn, called by the Publish button.
//
// Side-effects on success:
//   • cacheInvalidate("videos") so feed/profile refresh.
//   • refreshUser()   — credit balance changed.
//   • resetFormAfterPublish() — clears title/duration/storage and
//     blows away the IndexedDB draft.
export const useRecorderPublish = ({
  user,
  token,
  refreshUser,
  videoTitle,
  videoDuration,
  videoStorage,
  videoFeatured,
  totalCost,
  location,
  recordedBlob,
  activeCommission,
  onVideoCreated,
  onCommissionDone,
  onOpenChange,
  setVideoTitle,
  setVideoDuration,
  setVideoStorage,
  setVideoFeatured,
  setLocation,
  discardRecording,
  stopCameraStream,
}) => {
  const [creating, setCreating] = useState(false);

  const resetFormAfterPublish = useCallback(async () => {
    setVideoTitle("");
    setVideoDuration(8);
    setVideoStorage(10);
    setVideoFeatured(false);
    setLocation(null);
    await clearDraft();
    discardRecording();
    stopCameraStream();
  }, [
    setVideoTitle, setVideoDuration, setVideoStorage,
    setVideoFeatured, setLocation, discardRecording, stopCameraStream,
  ]);

  const handlePublish = useCallback(async () => {
    if (!videoTitle) { toast.error("Please enter a title"); return; }
    if (!recordedBlob) { toast.error("Please record a video first"); return; }
    const finalLocation = location || DEFAULT_LOCATION;
    // Jun 15, 2026 — city/country are now required. Block client-side
    // so the user gets an actionable error in the form instead of the
    // 400 from /videos/upload after the bandwidth burn.
    if (!(finalLocation.city || "").trim() || !(finalLocation.country || "").trim()) {
      toast.error("Please add a city before publishing. Tap Retry on the Location row or type one in.");
      return;
    }

    // ---- Commission path ----
    if (activeCommission) {
      setCreating(true);
      try {
        const formData = new FormData();
        const ext = (recordedBlob.type || "").includes("mp4") ? "mp4" : "webm";
        formData.append("file", recordedBlob, `recording.${ext}`);
        formData.append("title", videoTitle);
        const res = await api.post(
          `/commissions/${activeCommission.id}/submit`,
          formData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "multipart/form-data",
            },
          }
        );
        toast.success(
          `Commission fulfilled! +${(res.data.bounty_credits || 0).toFixed(2)} bounty credits`
        );
        onVideoCreated?.(res.data.video);
        cacheInvalidate("videos");
        cacheInvalidate("followingFeed");
        if (user?.username) cacheInvalidate(`profile:${user.username.toLowerCase()}`);
        await refreshUser?.();
        onCommissionDone?.();
        await resetFormAfterPublish();
        onOpenChange(false);
      } catch (err) {
        toast.error(err.response?.data?.detail || "Failed to publish commission");
      }
      setCreating(false);
      return;
    }

    // ---- Free-form path ----
    if (user.credits < totalCost) {
      toast.error(`Insufficient credits. Need ${totalCost.toFixed(2)} credits.`);
      return;
    }
    setCreating(true);
    try {
      const formData = new FormData();
      const ext = (recordedBlob.type || "").includes("mp4") ? "mp4" : "webm";
      formData.append("file", recordedBlob, `recording.${ext}`);
      formData.append("title", videoTitle);
      formData.append("location", JSON.stringify(finalLocation));
      formData.append("duration", String(videoDuration));
      formData.append("storage_days", String(videoStorage));
      formData.append("featured", String(videoFeatured));
      const res = await api.post(`/videos/upload`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });
      toast.success(`Video published! Cost: ${res.data.total_cost.toFixed(2)} credits`);
      onVideoCreated?.(res.data.video);
      cacheInvalidate("videos");
      cacheInvalidate("followingFeed");
      if (user?.username) cacheInvalidate(`profile:${user.username.toLowerCase()}`);
      await refreshUser?.();
      await resetFormAfterPublish();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to upload video");
    }
    setCreating(false);
  }, [
    user, token, videoTitle, videoDuration, videoStorage, videoFeatured,
    totalCost, location, recordedBlob, activeCommission, refreshUser,
    onVideoCreated, onCommissionDone, onOpenChange, resetFormAfterPublish,
  ]);

  return { creating, handlePublish, resetFormAfterPublish };
};
